interface TermsAndConditionsModalProps {
  isOpen: boolean;
  onClose: () => void;
}

function TermsAndConditionsModal({
  isOpen,
  onClose,
}: TermsAndConditionsModalProps) {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[1000] flex items-end sm:items-center justify-center">
      <div
        className="absolute inset-0 bg-black/40"
        onClick={onClose}
        aria-hidden
      />
      <div className="relative w-full sm:max-w-[600px] max-h-[85vh] bg-bg-page-body rounded-t-[16px] sm:rounded-[16px] shadow-xl flex flex-col overflow-hidden mx-0 sm:mx-[16px]">
        <div className="flex items-center justify-between px-[16px] py-[14px] border-b border-border-subtle shrink-0">
          <h2 className="text-text-base text-[16px] font-bold">
            Terms & Conditions
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="material-symbols-outlined text-[22px] text-text-subtle"
          >
            close
          </button>
        </div>

        <div className="px-[16px] py-[16px] overflow-y-auto">
          <div className="text-text-base space-y-4">
            <div>
              <h3 className="text-[14px] font-bold mb-2">
                END-USER TERMS & CONDITIONS
              </h3>
              <p className="text-[11px] text-text-subtle mb-1">
                On-Ramp, Off-Ramp and Transfer Services
              </p>
              <p className="text-[11px] text-text-subtle mb-2">
                Effective Date: 1 July 2026
                <br />
                Last Updated: 30 September 2026
              </p>
            </div>

            <div className="space-y-4 text-[12px] leading-relaxed">
              <p>
                These Terms &amp; Conditions (&ldquo;Terms&rdquo;) govern the use
                of fiat-to-crypto (&ldquo;On-Ramp&rdquo;), crypto-to-fiat
                (&ldquo;Off-Ramp&rdquo;), virtual currency transfer, and related
                services (collectively, the &ldquo;Services&rdquo;) provided
                through the infrastructure of <strong>TYLT FINTECH INC.</strong>{" "}
                (&ldquo;Tylt&rdquo;, &ldquo;we&rdquo;, &ldquo;us&rdquo;, or
                &ldquo;our&rdquo;).
              </p>
              <p>
                TYLT FINTECH INC. is a company incorporated in British Columbia,
                Canada, having its registered address at:
              </p>
              <p>2-1130 HACHEY AVE</p>
              <p>COQUITLAM, BC</p>
              <p>V3K 2H4</p>
              <p>CANADA</p>
              <p>
                TYLT FINTECH INC. is registered as a Money Services Business
                (&ldquo;MSB&rdquo;) with the Financial Transactions and Reports
                Analysis Centre of Canada (&ldquo;FINTRAC&rdquo;) under MSB
                Registration No. C10001670.
              </p>
              <p>Company Registration No.: BC1571749</p>
              <p>
                By accessing or using the Services, initiating a transaction, or
                clicking an acceptance or confirmation button presented in
                connection with the Services, the User agrees to be bound by
                these Terms.
              </p>

              <div>
                <h4 className="text-[13px] font-semibold mb-2">
                  1. Scope of Services
                </h4>
                <p>Tylt provides infrastructure enabling:</p>
                <p className="mt-3 font-semibold">1.1 On-Ramp</p>
                <p className="mt-2">
                  The conversion of fiat currency into virtual currency or
                  crypto-assets, including supported stablecoins such as USDT or
                  USDC.
                </p>
                <p className="mt-3 font-semibold">1.2 Off-Ramp</p>
                <p className="mt-2">
                  The conversion of supported virtual currency or crypto-assets
                  into fiat currency.
                </p>
                <p className="mt-3 font-semibold">1.3 Transfer Services</p>
                <p className="mt-2">
                  Where available, Tylt may facilitate the transfer or remittance
                  of supported virtual currency / crypto-assets or fiat funds to
                  a destination designated or authorised by the User.
                </p>
                <p className="mt-2">
                  The availability of any particular Service, asset, currency,
                  payment method, payment rail, wallet type, transfer
                  functionality, or destination is subject to product
                  availability, User eligibility, jurisdiction, regulatory
                  restrictions, compliance requirements, and Tylt&rsquo;s risk
                  controls.
                </p>
                <p className="mt-2">
                  Tylt operates at the virtual currency and crypto-asset
                  infrastructure layer. Fiat collection, holding, processing,
                  settlement and disbursement may be performed by regulated
                  third-party payment service providers, financial institutions,
                  banking partners, payment processors, or other approved service
                  providers.
                </p>
                <p className="mt-2">
                  Tylt is not a bank, credit union, trust company, securities
                  dealer, payment institution or e-money issuer and does not
                  provide fiat deposit or custody services.
                </p>
                <p className="mt-2">
                  Nothing in these Terms shall be interpreted as representing
                  that funds held or processed through a third-party payment
                  provider are deposits with Tylt or are protected by any deposit
                  insurance scheme applicable to bank deposits.
                </p>
              </div>

              <div>
                <h4 className="text-[13px] font-semibold mb-2">
                  2. Access Model
                </h4>
                <p>Users may access the Services through:</p>
                <ul className="list-disc list-inside ml-4 mt-2 space-y-1">
                  <li>
                    Tylt&rsquo;s website and exchange interface at
                    exchange.tylt.money; or
                  </li>
                  <li>
                    Approved integrators, partners, affiliates, platforms,
                    applications or other third-party integrations through which
                    Tylt&rsquo;s Services are made available.
                  </li>
                </ul>
                <p className="mt-2">
                  Access through an integrator, partner, affiliate or other third
                  party does not change the application of these Terms to the
                  Services provided by Tylt.
                </p>
                <p className="mt-2">
                  A User may also be subject to separate contractual terms
                  imposed by the relevant integrator, partner, affiliate,
                  financial institution, payment service provider or other third
                  party.
                </p>
                <p className="mt-2">
                  Tylt is not responsible for separate products or services
                  provided independently by such third parties.
                </p>
                <p className="mt-2">
                  Tylt may require Users to complete identity verification,
                  compliance checks, transaction verification, source-of-funds
                  verification, wallet verification, or other onboarding
                  procedures directly with Tylt regardless of the channel through
                  which the User accesses the Services.
                </p>
                <p className="mt-2">
                  Access to particular Services, payment methods, virtual
                  currencies / crypto-assets, transfer functionality and
                  jurisdictions may vary and is subject to availability,
                  eligibility requirements, compliance requirements, regulatory
                  restrictions and Tylt&rsquo;s applicable risk controls.
                </p>
              </div>

              <div>
                <h4 className="text-[13px] font-semibold mb-2">
                  3. Geographic Availability and Eligibility
                </h4>
                <p>The Services are not available in every country or jurisdiction.</p>
                <p className="mt-2">
                  Tylt may determine, restrict or modify the geographic
                  availability of any Service, payment rail, payment method,
                  virtual currency / crypto-asset, transfer destination or
                  functionality at any time based on:
                </p>
                <ul className="list-disc list-inside ml-4 mt-2 space-y-1">
                  <li>Applicable laws or regulations;</li>
                  <li>Licensing or registration requirements;</li>
                  <li>Payment provider or banking restrictions;</li>
                  <li>Sanctions requirements;</li>
                  <li>AML/ATF requirements;</li>
                  <li>Regulatory guidance;</li>
                  <li>Partner availability;</li>
                  <li>Product availability;</li>
                  <li>Risk assessments; or</li>
                  <li>Other legal, compliance or operational considerations.</li>
                </ul>
                <p className="mt-2">
                  The User is responsible for ensuring that accessing and using
                  the Services is lawful in the jurisdiction in which the User
                  resides, is domiciled, is established and from which the User
                  accesses the Services.
                </p>
                <p className="mt-2">
                  Tylt may use information including IP address, device
                  information, account information, payment information, KYC
                  information, country of residence, country of incorporation or
                  establishment, bank account jurisdiction and other information
                  to determine eligibility.
                </p>
                <p className="mt-2">
                  Users must not use a VPN, proxy service, false address, false
                  entity information, location-masking technology or other method
                  to circumvent geographic or eligibility restrictions.
                </p>
                <p className="mt-2">
                  Tylt may decline, suspend, restrict or terminate access where
                  it reasonably believes that a User is accessing or attempting
                  to access a Service from an unsupported or restricted
                  jurisdiction.
                </p>
              </div>

              <div>
                <h4 className="text-[13px] font-semibold mb-2">
                  4. EU/EEA and United Kingdom Access Restrictions
                </h4>
                <p className="font-semibold">4.1 General Restriction</p>
                <p className="mt-2">
                  Certain Services, including the Open Banking payment method,
                  are subject to specific geographic access restrictions.
                </p>
                <p className="mt-2">
                  Tylt does not market, promote, solicit or actively offer the
                  applicable Open Banking Services to:
                </p>
                <ul className="list-disc list-inside ml-4 mt-2 space-y-1">
                  <li>
                    Individuals residing in, domiciled in or accessing the
                    Service from the European Union or European Economic Area
                    (&ldquo;EU/EEA&rdquo;);
                  </li>
                  <li>
                    Individuals residing in, domiciled in or accessing the
                    Service from the United Kingdom (&ldquo;UK&rdquo;);
                  </li>
                  <li>
                    Businesses or other legal entities incorporated, registered,
                    established or headquartered in the EU/EEA or UK; or
                  </li>
                  <li>
                    Businesses or other legal entities whose principal place of
                    business is within the EU/EEA or UK.
                  </li>
                </ul>
                <p className="mt-2">
                  Availability of other Tylt Services to Users in any
                  jurisdiction remains subject to the product-specific
                  eligibility requirements and regulatory restrictions applicable
                  to those Services.
                </p>
                <p className="mt-3 font-semibold">4.2 Individual Declaration</p>
                <p className="mt-2">
                  Where an individual is required to complete an eligibility
                  declaration before accessing the applicable Open Banking
                  Service, the individual must declare and confirm that:
                </p>
                <ul className="list-disc list-inside ml-4 mt-2 space-y-1">
                  <li>
                    The individual is not resident in, domiciled in, or currently
                    accessing the Service from a country within the EU/EEA or the
                    UK; and
                  </li>
                  <li>
                    The individual accessed the Service independently and
                    entirely on their own initiative and did not do so in
                    response to any advertisement, marketing communication,
                    promotional activity, recommendation, direct communication or
                    other solicitation by Tylt, its affiliates, representatives,
                    partners or anyone acting on Tylt&rsquo;s behalf.
                  </li>
                </ul>
                <p className="mt-3 font-semibold">
                  4.3 Business or Legal Entity Declaration
                </p>
                <p className="mt-2">
                  Where a person is acting on behalf of a business or other legal
                  entity, that person must declare and confirm that:
                </p>
                <ul className="list-disc list-inside ml-4 mt-2 space-y-1">
                  <li>
                    They are duly authorised to make the declaration and accept
                    the applicable conditions on behalf of the business or legal
                    entity;
                  </li>
                  <li>
                    The business or legal entity is not incorporated, registered,
                    established or headquartered in the EU/EEA or UK and does not
                    have its principal place of business within the EU/EEA or UK;
                  </li>
                  <li>
                    The business or legal entity independently approached and
                    accessed Tylt&rsquo;s Services entirely on its own initiative
                    and did not do so in response to any advertisement, marketing
                    communication, promotional activity, recommendation, direct
                    communication or other solicitation by Tylt, its affiliates,
                    representatives, partners or anyone acting on Tylt&rsquo;s
                    behalf;
                  </li>
                  <li>
                    The business or legal entity will not market, promote,
                    distribute or actively offer the applicable Open Banking
                    Services to individuals residing in or accessing the Service
                    from the EU/EEA or UK or to businesses established within the
                    EU/EEA or UK;
                  </li>
                  <li>
                    The business or legal entity will not enable or facilitate
                    access to the applicable payment method for an end user whom
                    it knows, or reasonably should know, resides in, is
                    established in or is accessing the Service from the EU/EEA or
                    UK;
                  </li>
                  <li>
                    The business or legal entity will maintain appropriate
                    customer-location, eligibility and access controls; and
                  </li>
                  <li>
                    The business or legal entity will provide supporting
                    information relating to such controls to Tylt upon reasonable
                    request.
                  </li>
                </ul>
                <p className="mt-3 font-semibold">4.4 Reliance on Declarations</p>
                <p className="mt-2">
                  Tylt may rely on declarations, information and documentation
                  provided by a User or a person acting on behalf of a business
                  or legal entity in determining eligibility.
                </p>
                <p className="mt-2">
                  All information and declarations provided must be accurate,
                  complete and not misleading.
                </p>
                <p className="mt-2">
                  Tylt may request additional information or documentation to
                  verify identity, residence, domicile, current location, place
                  of incorporation, place of establishment, principal place of
                  business, authority to act, the circumstances under which the
                  Service was accessed, or any other information reasonably
                  required to determine eligibility.
                </p>
                <p className="mt-2">
                  Where Tylt determines or reasonably suspects that any
                  declaration is inaccurate, incomplete or misleading, or that a
                  restricted Service is being accessed, marketed, offered,
                  distributed or otherwise made available within the EU/EEA or UK
                  contrary to these Terms, Tylt may decline, suspend or cancel
                  the transaction, disable the applicable payment method,
                  restrict or terminate access, request additional information,
                  or take any other action reasonably necessary to comply with
                  applicable law, regulation, contractual obligations or
                  Tylt&rsquo;s compliance framework.
                </p>
                <p className="mt-2">
                  Where a declaration is presented electronically, clicking
                  &ldquo;Confirm and Continue&rdquo;, or an equivalent
                  confirmation, constitutes confirmation that the User has read
                  and understood the declaration, has provided accurate
                  information, and is eligible and, where applicable, duly
                  authorised to proceed.
                </p>
                <p className="mt-2">
                  No declaration, contractual provision or disclaimer shall
                  override applicable law or deem a transaction or Service to
                  satisfy a regulatory requirement where the underlying facts do
                  not support that conclusion.
                </p>
              </div>

              <div>
                <h4 className="text-[13px] font-semibold mb-2">
                  5. Transaction Flow
                </h4>
                <p className="font-semibold">5.1 On-Ramp — Fiat to Crypto</p>
                <p className="mt-2">For an On-Ramp transaction:</p>
                <ul className="list-disc list-inside ml-4 mt-2 space-y-1">
                  <li>
                    The User initiates a fiat payment through an available
                    payment channel, financial institution, payment service
                    provider or other approved payment method;
                  </li>
                  <li>
                    The fiat funds are collected and processed by the relevant
                    payment service provider, financial institution or other
                    approved service provider;
                  </li>
                  <li>
                    Tylt and/or its service providers may conduct payment
                    verification, identity verification, sanctions screening,
                    transaction monitoring, fraud checks and other compliance
                    controls;
                  </li>
                  <li>
                    Upon successful receipt and verification of the fiat payment,
                    the applicable conversion is performed; and
                  </li>
                  <li>
                    The corresponding virtual currency / crypto-assets are
                    delivered, credited or otherwise made available in accordance
                    with the applicable transaction instructions.
                  </li>
                </ul>
                <p className="mt-2">
                  A payment being initiated or debited from a bank account does
                  not constitute final acceptance or completion of an On-Ramp
                  transaction.
                </p>
                <p className="mt-3 font-semibold">5.2 Off-Ramp — Crypto to Fiat</p>
                <p className="mt-2">For an Off-Ramp transaction:</p>
                <ul className="list-disc list-inside ml-4 mt-2 space-y-1">
                  <li>
                    The User transfers or makes available the applicable virtual
                    currency / crypto-assets to the wallet address or
                    infrastructure specified for the transaction;
                  </li>
                  <li>
                    The virtual currency / crypto-assets may be subject to
                    blockchain confirmations, sanctions screening, wallet
                    screening, blockchain analytics, Travel Rule requirements and
                    other compliance controls;
                  </li>
                  <li>
                    Following successful receipt, verification and approval, the
                    virtual currency / crypto-assets are converted into the
                    applicable fiat currency; and
                  </li>
                  <li>
                    The corresponding fiat amount is disbursed through an
                    available payment provider, financial institution, banking
                    partner or payment rail to the verified bank account, payment
                    account or other supported destination designated by the
                    User.
                  </li>
                </ul>
                <p className="mt-2">
                  Receipt of virtual currency / crypto-assets at a designated
                  wallet does not guarantee that the transaction will be
                  completed if the transaction subsequently fails a compliance,
                  sanctions, fraud, payment-integrity or other required review.
                </p>
              </div>

              <div>
                <h4 className="text-[13px] font-semibold mb-2">
                  6. Transfer of Funds
                </h4>
                <p className="font-semibold">6.1 On-Ramp Transfers</p>
                <p className="mt-2">
                  Virtual currency / crypto-assets generated through an On-Ramp
                  transaction may, subject to product availability and
                  eligibility requirements, be transferred to a self-hosted or
                  unhosted wallet controlled by the User, credited to a custodial
                  wallet, account or ledger balance made available through
                  Tylt&rsquo;s infrastructure or an approved integration, or
                  transferred or remitted, at the User&rsquo;s instruction, to
                  another wallet address designated by the User.
                </p>
                <p className="mt-2">
                  A wallet destination presented through an integration shall
                  only be used where the User has selected, specified, authorised
                  or otherwise confirmed that destination in connection with the
                  transaction. Transfers remain subject to product availability,
                  jurisdiction, sanctions, Travel Rule, blockchain analytics,
                  wallet screening, AML/ATF requirements and Tylt&rsquo;s
                  compliance and risk controls.
                </p>
                <p className="mt-2">
                  Tylt does not guarantee that transfers to every wallet, wallet
                  provider, exchange, platform, protocol, blockchain or
                  jurisdiction will be supported.
                </p>
                <p className="mt-3 font-semibold">6.2 Off-Ramp Transfers</p>
                <p className="mt-2">
                  Virtual currency / crypto-assets may be received from the User
                  or from a wallet or account designated or authorised by the
                  User. Tylt may require information regarding ownership or
                  control of the originating wallet and concerning another person
                  or entity involved in a transfer where required for regulatory
                  or compliance purposes. Following successful receipt, screening,
                  verification and processing, the assets may be converted and
                  the corresponding fiat amount disbursed to the verified
                  destination designated by the User.
                </p>
                <p className="mt-2">
                  Tylt reserves the right to restrict, delay, reject, suspend or
                  refuse transfers to or from particular wallets, wallet types,
                  accounts, counterparties, platforms, protocols, virtual asset
                  service providers, jurisdictions or blockchain addresses,
                  including self-hosted or unhosted wallets, based on sanctions
                  screening, blockchain analytics, Travel Rule obligations,
                  AML/ATF requirements, fraud controls, regulatory requirements or
                  Tylt&rsquo;s risk assessment.
                </p>
              </div>

              <div>
                <h4 className="text-[13px] font-semibold mb-2">
                  7. User Representations and Warranties
                </h4>
                <p>The User represents, warrants and confirms that:</p>
                <ul className="list-disc list-inside ml-4 mt-2 space-y-1">
                  <li>
                    All fiat funds and virtual currency / crypto-assets used in
                    connection with the Services originate from lawful sources;
                  </li>
                  <li>
                    The User is acting on their own behalf or has proper legal
                    authority to act for the person or legal entity concerned;
                  </li>
                  <li>
                    All information provided to Tylt or its service providers is
                    accurate, current, complete and not misleading;
                  </li>
                  <li>
                    The User will provide additional information or documentation
                    reasonably requested by Tylt;
                  </li>
                  <li>
                    The User&rsquo;s use of the Services complies with all laws
                    and regulations applicable to the User and the transaction;
                  </li>
                  <li>
                    The User is not a sanctioned or prohibited person and is not
                    acting directly or indirectly on behalf of a sanctioned or
                    prohibited person, entity, wallet or jurisdiction;
                  </li>
                  <li>
                    The User will not use the Services for money laundering,
                    terrorist financing, proliferation financing, sanctions
                    evasion, fraud, scams, illegal gambling, market abuse or
                    other unlawful activity;
                  </li>
                  <li>The User has full legal capacity to use the Services;</li>
                  <li>
                    Where acting for a business or legal entity, the User has
                    authority to bind that business or legal entity;
                  </li>
                  <li>
                    Any wallet or payment destination specified or confirmed by
                    the User is intended by the User for the relevant
                    transaction; and
                  </li>
                  <li>
                    The User will not attempt to bypass geographic, compliance,
                    transaction, account or technical restrictions imposed by
                    Tylt.
                  </li>
                </ul>
              </div>

              <div>
                <h4 className="text-[13px] font-semibold mb-2">
                  8. KYC, AML/ATF and Compliance
                </h4>
                <p>
                  Identity verification, know-your-customer (&ldquo;KYC&rdquo;)
                  checks, business verification and enhanced due diligence may be
                  required. Transactions may be screened and monitored under
                  applicable anti-money laundering, anti-terrorist financing,
                  sanctions, proceeds-of-crime and related laws. Tylt may verify
                  source of funds or source of wealth and screen Users,
                  counterparties, wallets, transactions and related persons.
                  Past approval of a User or transaction does not guarantee
                  approval of any subsequent transaction.
                </p>
                <p className="mt-2">
                  Tylt may delay, reject, suspend, restrict, review or cancel a
                  transaction, request additional information or identity
                  verification, require proof of ownership or control of a wallet
                  or payment account, refuse unacceptable wallet or counterparty
                  exposure, or take any other action reasonably required under
                  applicable law or Tylt&rsquo;s compliance framework.
                </p>
                <p className="mt-2">
                  Tylt may report suspicious transactions, attempted suspicious
                  transactions, large virtual currency transactions, terrorist
                  property or other reportable matters to FINTRAC or another
                  competent authority where required by law. Where legally
                  prohibited from doing so, Tylt may be unable to inform the User
                  that a report has been made. Tylt will retain records in
                  accordance with applicable legal and regulatory requirements.
                </p>
              </div>

              <div>
                <h4 className="text-[13px] font-semibold mb-2">
                  9. Travel Rule and Wallet Information
                </h4>
                <p>
                  Where applicable, Tylt may collect, verify, transmit, receive,
                  request or retain information concerning the originator and
                  beneficiary of a virtual currency transfer, including legal
                  name, wallet address, account or transaction reference,
                  address, date of birth, country of residence or incorporation,
                  customer identification, beneficiary information, virtual asset
                  service provider information, purpose of the transaction, and
                  other information required under applicable Travel Rule,
                  AML/ATF or sanctions requirements.
                </p>
                <p className="mt-2">
                  Tylt may require Users to identify whether a wallet is
                  self-hosted or unhosted, controlled by another person,
                  custodial, an exchange or virtual asset service provider
                  wallet, or another supported wallet. Tylt may refuse, delay or
                  reject a transfer where required information is missing,
                  incomplete, inaccurate, unverifiable or presents an
                  unacceptable compliance risk.
                </p>
              </div>

              <div>
                <h4 className="text-[13px] font-semibold mb-2">
                  10. Name Matching and Payment Integrity
                </h4>
                <p>
                  For applicable transactions, the User&rsquo;s verified identity
                  must correspond with the identity associated with the
                  originating or beneficiary payment account where required by
                  Tylt&rsquo;s payment-integrity controls. Tylt may prohibit or
                  restrict payments made using accounts belonging to unrelated
                  third parties unless expressly permitted for the particular
                  product and transaction structure.
                </p>
                <p className="mt-3 font-semibold">10.1 Mismatch Handling</p>
                <p className="mt-2">
                  Where a name mismatch, unauthorised third-party payment,
                  payment-integrity issue or other discrepancy is identified, the
                  transaction may be delayed, rejected, reversed or cancelled,
                  additional information may be requested, and funds may, where
                  possible, be returned to the original source. A processing,
                  administrative, investigation, bank-return or similar fee may
                  apply, and may be up to 5% of the transaction amount where
                  disclosed or otherwise permitted under applicable law. Tylt may
                  restrict further transactions involving the User, payment
                  account, wallet or related party. Any refund or return remains
                  subject to payment-provider requirements, banking requirements,
                  compliance review and operational feasibility.
                </p>
              </div>

              <div>
                <h4 className="text-[13px] font-semibold mb-2">
                  11. Fees, Rates and Pricing
                </h4>
                <p>
                  Exchange rates are dynamic and may change before execution.
                  Quotes may be valid only for the period displayed. Fees may
                  include exchange spreads, network fees, blockchain gas fees,
                  service fees, payment-provider fees, bank charges, refund fees,
                  return fees, chargeback fees and other applicable costs.
                  Different payment methods or jurisdictions may have different
                  fees. The final amount delivered or disbursed may differ from
                  an indicative amount due to exchange-rate movement, network
                  costs, banking charges, compliance delays or third-party
                  processing costs. Applicable transaction fees will be disclosed
                  prior to confirmation where required and reasonably practicable.
                </p>
                <p className="mt-2">
                  Where the User confirms a transaction after being shown the
                  applicable amount, rate and fees, the User authorises Tylt and
                  its service providers to execute the transaction on that basis.
                </p>
              </div>

              <div>
                <h4 className="text-[13px] font-semibold mb-2">
                  12. Settlement and Timing
                </h4>
                <p>
                  Transaction and settlement times may depend on blockchain
                  confirmations, wallet screening, blockchain analytics, Travel
                  Rule checks, fraud monitoring, KYC or enhanced due diligence,
                  payment-provider, banking-partner and financial-institution
                  processing, compliance review, liquidity, banking hours,
                  weekends and public holidays, payment-rail and network
                  availability, and other circumstances beyond Tylt&rsquo;s
                  reasonable control.
                </p>
                <p className="mt-2">
                  Any processing or settlement time displayed is an estimate
                  unless expressly stated otherwise. Tylt does not guarantee
                  instant settlement or completion within a specific timeframe
                  unless expressly agreed in writing for a particular product or
                  transaction.
                </p>
              </div>

              <div>
                <h4 className="text-[13px] font-semibold mb-2">
                  13. Third-Party Dependencies
                </h4>
                <p>
                  The Services may depend upon payment service providers,
                  financial institutions, banking partners, payment processors,
                  blockchain networks, wallet infrastructure providers, liquidity
                  providers, virtual asset service providers, blockchain analytics
                  providers, identity-verification providers, compliance service
                  providers and technology service providers. Tylt may transmit
                  transaction instructions or information to such providers where
                  necessary to provide the Services.
                </p>
                <p className="mt-2">
                  Tylt does not control the operation, availability, settlement
                  schedules or compliance decisions of independent third parties.
                  To the maximum extent permitted by applicable law, Tylt shall
                  not be liable for failures, outages, delays, reversals,
                  rejections, downtime, freezes or errors caused by independent
                  third parties unless directly caused by Tylt&rsquo;s breach of
                  applicable law or contractual obligations.
                </p>
              </div>

              <div>
                <h4 className="text-[13px] font-semibold mb-2">
                  14. Risk Disclosure
                </h4>
                <p>
                  Users acknowledge that virtual currency / crypto-assets may be
                  volatile, blockchain transactions may be irreversible, incorrect
                  networks or addresses may result in permanent loss, transactions
                  may be delayed or rejected for compliance reasons, regulation
                  may change, stablecoins may lose their peg, banks and payment
                  providers may reject or delay payments, and Tylt does not
                  control blockchain networks or independent financial
                  institutions and does not provide investment, legal, tax,
                  financial, accounting or trading advice.
                </p>
                <p className="mt-2">
                  The User is responsible for assessing whether a transaction and
                  the relevant virtual currency / crypto-asset are appropriate for
                  the User.
                </p>
              </div>

              <div>
                <h4 className="text-[13px] font-semibold mb-2">
                  15. Transaction Instructions and User Responsibility
                </h4>
                <p>
                  The User is responsible for reviewing transaction details before
                  confirmation, including amount, currency, virtual currency /
                  crypto-asset, blockchain network, wallet address, bank account
                  details, beneficiary details, payment destination, exchange rate
                  and applicable fees. Tylt may rely on transaction instructions
                  submitted or confirmed by the User.
                </p>
                <p className="mt-2">
                  Tylt is not responsible for losses resulting from incorrect
                  information supplied or confirmed by the User except where the
                  loss is directly caused by Tylt&rsquo;s failure to execute a
                  valid instruction correctly. A User must immediately notify Tylt
                  if the User becomes aware of unauthorised account access,
                  compromised credentials or a potentially fraudulent transaction.
                </p>
              </div>

              <div>
                <h4 className="text-[13px] font-semibold mb-2">
                  16. Limitation of Liability
                </h4>
                <p>
                  To the maximum extent permitted by applicable law, Tylt shall
                  not be liable for loss arising from incorrect User-provided
                  information, incorrect wallet addresses or networks, incorrect
                  payment information, third-party or banking failures, blockchain
                  delays, market losses, slippage, exchange-rate movement,
                  compliance-driven restrictions undertaken in good faith, fraud
                  or account compromise not caused by Tylt, actions of independent
                  providers, regulators or authorities, force majeure, or
                  circumstances outside Tylt&rsquo;s reasonable control.
                </p>
                <p className="mt-2">
                  Nothing in these Terms excludes or limits liability that cannot
                  lawfully be excluded or limited. Where applicable consumer
                  protection legislation or other mandatory law grants the User
                  rights that cannot be waived contractually, those rights will
                  continue to apply.
                </p>
              </div>

              <div>
                <h4 className="text-[13px] font-semibold mb-2">
                  17. Transaction Finality, Returns and Refunds
                </h4>
                <p>
                  Virtual currency / crypto-asset transactions are generally final
                  once broadcast or confirmed on the relevant blockchain. Tylt may
                  be unable to cancel, reverse or recover a blockchain
                  transaction after it has been transmitted. Fiat transfers remain
                  subject to the applicable payment provider, bank, payment rail
                  and financial institution. Refunds are not guaranteed after a
                  transaction has been executed, converted, settled or transmitted
                  on-chain.
                </p>
                <p className="mt-2">
                  Where a refund, return or reversal is possible, it may be
                  subject to processing, administrative, bank-return,
                  payment-provider, network, gas, exchange-rate, conversion and
                  compliance costs. Where practicable, refunds of fiat payments
                  will generally be made to the original payment source. Tylt may
                  decline a requested alternative refund destination where doing so
                  would create an unacceptable fraud, AML/ATF, sanctions or
                  payment-integrity risk.
                </p>
              </div>

              <div>
                <h4 className="text-[13px] font-semibold mb-2">
                  18. Suspension, Rejection and Termination
                </h4>
                <p>
                  Tylt may suspend, restrict, reject or terminate a User&rsquo;s
                  access where required by law, a court, regulator, law
                  enforcement or sanctions obligation; where suspicious,
                  fraudulent, abusive or high-risk activity is detected; where the
                  User breaches these Terms or provides false or incomplete
                  information; where the User refuses reasonably requested
                  compliance information; where a transaction involves prohibited
                  activity or unacceptable risk; where verification fails; where
                  the User attempts to circumvent restrictions; where continued
                  service could expose Tylt or its providers to legal, financial,
                  regulatory, reputational or operational risk; where a required
                  provider ceases support; or where Tylt discontinues the relevant
                  product, payment method or jurisdiction.
                </p>
                <p className="mt-2">
                  Where legally and operationally possible, any remaining
                  transaction or balance will be handled in accordance with
                  applicable law, contractual obligations and compliance
                  requirements.
                </p>
              </div>

              <div>
                <h4 className="text-[13px] font-semibold mb-2">
                  19. Prohibited Use
                </h4>
                <p>
                  Users must not use, attempt to use, or facilitate use of the
                  Services in connection with money laundering, terrorist
                  financing, proliferation financing, sanctions evasion, fraud,
                  scams, phishing, identity theft, stolen funds, darknet markets,
                  ransomware, illegal or unlicensed gambling, unlicensed or
                  unlawful financial services, human trafficking, weapons
                  trafficking, child sexual exploitation or abuse, market
                  manipulation, cybercrime, theft or misappropriation,
                  circumvention of regulatory restrictions, transactions involving
                  sanctioned persons, entities or jurisdictions, transactions
                  involving assets known or reasonably suspected to represent
                  proceeds of crime, or any activity prohibited by applicable law
                  or Tylt&rsquo;s applicable risk policies.
                </p>
                <p className="mt-2">
                  Tylt may maintain additional internal restrictions based on
                  jurisdiction, business activity, wallet exposure, transaction
                  type or other risk considerations.
                </p>
              </div>

              <div>
                <h4 className="text-[13px] font-semibold mb-2">
                  20. Data, Privacy and Recordkeeping
                </h4>
                <p>
                  Tylt may collect, use, process, store and retain information
                  relating to identity, contact details, device and access
                  information, IP address and location indicators, transactions,
                  payments, bank accounts, wallet addresses, blockchain activity,
                  KYC, compliance assessments, sanctions screening, fraud
                  monitoring, Travel Rule information, and other information
                  reasonably required to provide or protect the Services.
                </p>
                <p className="mt-2">
                  Information may be processed to provide the Services, execute
                  transactions, complete KYC, meet AML/ATF, sanctions and Travel
                  Rule obligations, prevent fraud, manage risk, report to
                  regulators, resolve disputes, audit, keep records and comply
                  with legal obligations. Information may be shared, where
                  required or permitted, with payment providers, banks,
                  integrators, partners, affiliates, service providers,
                  verification and compliance vendors, professional advisers,
                  auditors, regulators, law enforcement, courts and other
                  competent authorities.
                </p>
                <p className="mt-2">
                  Tylt may retain information for the period required under
                  applicable Canadian laws and regulations and for any longer
                  period reasonably necessary to establish, exercise or defend
                  legal claims. Use of personal information may also be subject to
                  Tylt&rsquo;s applicable Privacy Policy.
                </p>
              </div>

              <div>
                <h4 className="text-[13px] font-semibold mb-2">21. Security</h4>
                <p>
                  Users are responsible for maintaining the security of their
                  devices, email accounts, credentials and any authentication
                  mechanism used to access the Services. Users must not share
                  credentials, permit unauthorised access, interfere with
                  Tylt&rsquo;s systems, circumvent access controls, introduce
                  malware, or attempt unauthorised access to Tylt infrastructure.
                  Tylt may require additional authentication or security
                  procedures where it considers such measures necessary.
                </p>
              </div>

              <div>
                <h4 className="text-[13px] font-semibold mb-2">22. No Advice</h4>
                <p>
                  Tylt does not provide investment, trading, tax, legal,
                  financial, accounting or regulatory advice. Information provided
                  through the Services is for operational and transactional
                  purposes only. Users are responsible for independently
                  determining whether a transaction is lawful, suitable and
                  appropriate for them.
                </p>
              </div>

              <div>
                <h4 className="text-[13px] font-semibold mb-2">
                  23. Changes to Services
                </h4>
                <p>
                  Tylt may add, modify, restrict, suspend or discontinue a
                  Service, payment method, payment rail, supported currency or
                  virtual currency / crypto-asset, blockchain network, wallet
                  type, transfer functionality, geographic market or other
                  functionality where reasonably required for legal, regulatory,
                  compliance, commercial, security, technical or operational
                  reasons. Tylt does not guarantee the continuing availability of
                  any particular product, currency, asset, provider, network,
                  payment rail or jurisdiction.
                </p>
              </div>

              <div>
                <h4 className="text-[13px] font-semibold mb-2">
                  24. Amendments to These Terms
                </h4>
                <p>
                  Tylt may update these Terms from time to time. Updated Terms
                  will apply from the date stated in the updated version or
                  otherwise notified to the User, subject to any notice
                  requirements imposed by applicable law. Where required, Tylt may
                  ask the User to expressly accept updated Terms before continuing
                  to use the Services. Otherwise, continued use of the Services
                  after the applicable effective date constitutes acceptance of
                  the updated Terms to the extent permitted by law.
                </p>
              </div>

              <div>
                <h4 className="text-[13px] font-semibold mb-2">
                  25. Severability
                </h4>
                <p>
                  If any provision of these Terms is determined to be unlawful,
                  invalid or unenforceable, that provision shall be interpreted or
                  modified to the minimum extent necessary to make it enforceable
                  where permitted by law. If that is not possible, the affected
                  provision shall be severed. The remaining provisions will
                  continue in full force and effect.
                </p>
              </div>

              <div>
                <h4 className="text-[13px] font-semibold mb-2">26. No Waiver</h4>
                <p>
                  A failure or delay by Tylt in exercising a right or remedy under
                  these Terms shall not constitute a waiver of that right or
                  remedy. A waiver in respect of one matter shall not constitute a
                  waiver in respect of any subsequent matter.
                </p>
              </div>

              <div>
                <h4 className="text-[13px] font-semibold mb-2">
                  27. Governing Law and Jurisdiction
                </h4>
                <p>
                  These Terms are governed by the laws of the Province of British
                  Columbia and the federal laws of Canada applicable therein.
                  Subject to any mandatory consumer protection, jurisdictional or
                  other rights that cannot lawfully be excluded, the courts of
                  British Columbia, Canada shall have jurisdiction over disputes
                  arising out of or in connection with these Terms. Nothing in
                  this section limits any right that a User may have under
                  mandatory applicable law to bring proceedings in another
                  jurisdiction.
                </p>
              </div>

              <div>
                <h4 className="text-[13px] font-semibold mb-2">28. Contact</h4>
                <p>
                  <strong>TYLT FINTECH INC.</strong>
                </p>
                <p className="mt-2">Registered Address:</p>
                <p>2-1130 HACHEY AVE</p>
                <p>COQUITLAM, BC</p>
                <p>V3K 2H4</p>
                <p>CANADA</p>
                <p className="mt-2">Company Registration No.: BC1571749</p>
                <p>FINTRAC MSB Registration No.: C10001670</p>
                <p>
                  Compliance:{" "}
                  <a
                    href="mailto:compliance@tylt.money"
                    className="text-icon-blue-dark underline"
                  >
                    compliance@tylt.money
                  </a>
                </p>
                <p>
                  Website:{" "}
                  <a
                    href="https://www.tylt.money"
                    className="text-icon-blue-dark underline"
                    target="_blank"
                    rel="noreferrer"
                  >
                    www.tylt.money
                  </a>
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default TermsAndConditionsModal;
